import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, Sparkles, Target } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  resolveThemePalette,
  type AssistantPersonaId,
  type GamificationMode,
  type ThemePaletteId,
} from '@/lib/portal-theme';

export interface OpsChecklistCoachProps {
  assistantPersona: AssistantPersonaId;
  gamificationMode: GamificationMode;
  palette: ThemePaletteId;
  completed: number;
  total: number;
  percent: number;
  nextStepLabel?: string;
  contextLabel?: string;
  compact?: boolean;
}

const PERSONA_LABELS: Record<AssistantPersonaId, string> = {
  boox_bot: 'Boox Bot',
  port_pilot: 'Port Pilot',
  yard_ranger: 'Yard Ranger',
  ledger_keeper: 'Ledger Keeper',
  lion_cub: 'Lion Cub',
};

function resolveCoachState(mode: GamificationMode, percent: number) {
  if (percent >= 100) return 'complete';
  if (mode === 'training' || mode === 'playful') return 'guided';
  return 'focus';
}

export function OpsChecklistCoach({
  assistantPersona,
  gamificationMode,
  palette,
  completed,
  total,
  percent,
  nextStepLabel,
  contextLabel = 'Workflow',
  compact = false,
}: OpsChecklistCoachProps) {
  const { t } = useTranslation();
  const themePalette = resolveThemePalette(palette);
  const coachState = resolveCoachState(gamificationMode, percent);
  const remaining = Math.max(0, total - completed);

  const message = useMemo(() => {
    if (percent >= 100) {
      return t('opsChecklistCoach.complete', 'Checklist complete. Review evidence and hand over when ready.');
    }
    if (nextStepLabel && (gamificationMode === 'training' || gamificationMode === 'playful')) {
      return t('opsChecklistCoach.guidedNextStep', {
        defaultValue: 'Follow the runbook: {{step}}',
        step: nextStepLabel,
      });
    }
    if (nextStepLabel) {
      return t('opsChecklistCoach.nextStep', {
        defaultValue: 'Next checkpoint: {{step}}',
        step: nextStepLabel,
      });
    }
    return t('opsChecklistCoach.keepAligned', 'Keep the checklist aligned with the live operation.');
  }, [gamificationMode, nextStepLabel, percent, t]);

  if (gamificationMode === 'off') return null;

  return (
    <div
      className={cn(
        'mt-3 overflow-hidden rounded-lg border px-3 py-2',
        compact && 'mt-2',
      )}
      style={{
        borderColor: `${themePalette.accent}33`,
        background: `linear-gradient(135deg, ${themePalette.surface} 0%, rgba(255,255,255,0.94) 100%)`,
      }}
    >
      <div className="flex items-start gap-2">
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
          style={{ background: `${themePalette.accent}1f`, color: themePalette.motifMain }}
          aria-hidden="true"
        >
          {coachState === 'complete'
            ? <Sparkles className="h-4 w-4" />
            : coachState === 'guided'
              ? <Target className="h-4 w-4" />
              : <Bot className="h-4 w-4" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="text-xs font-semibold text-foreground">
              {PERSONA_LABELS[assistantPersona]}
            </p>
            <Badge variant={coachState === 'complete' ? 'success' : 'secondary'} className="text-[10px]">
              {percent}%
            </Badge>
            {remaining > 0 && (
              <Badge variant="outline" className="text-[10px]">
                {t('opsChecklistCoach.remaining', { defaultValue: '{{count}} left', count: remaining })}
              </Badge>
            )}
            {(gamificationMode === 'training' || gamificationMode === 'playful') && (
              <Badge variant="outline" className="text-[10px]">
                {t('opsChecklistCoach.guided', 'Guided')}
              </Badge>
            )}
          </div>
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
            <span className="font-medium text-foreground/80">{contextLabel}:</span> {message}
          </p>
        </div>
      </div>
    </div>
  );
}
